// src/pages/OrganisationDetails.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

export default function OrganisationDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const assessmentType = location.state?.assessmentType || 'instant';

  const [formData, setFormData] = useState({
    organisationName: '',
    email: '',
    country: '',
    companyType: '',
    aiSystemStage: '',
    aiSystemDescription: '',
    industryFocus: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form options
  const countries = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 
    'France', 'Netherlands', 'Switzerland', 'Sweden', 'Denmark', 'Norway',
    'Singapore', 'Japan', 'South Korea', 'India', 'Other'
  ];

  const companyTypes = [
    'Startup (< 50 employees)',
    'Small Business (50-200 employees)',
    'Medium Enterprise (200-1000 employees)',
    'Large Enterprise (1000+ employees)',
    'Government Agency',
    'Non-profit Organization',
    'Academic Institution',
    'Consulting Firm'
  ];

  const aiSystemStages = [
    'Planning/Design Phase',
    'Development Phase',
    'Testing/Validation Phase',
    'Limited Deployment/Pilot',
    'Full Production Deployment',
    'Post-deployment Monitoring'
  ];

  const industryFocus = [
    'Healthcare & Life Sciences',
    'Financial Services',
    'Technology & Software',
    'Manufacturing & Industry 4.0',
    'Retail & E-commerce',
    'Transportation & Logistics',
    'Education',
    'Government & Public Sector',
    'Media & Entertainment',
    'Other'
  ];

  const steps = ['Choose Assessment', 'Organisation Details', 'RAISE Questions', 'Results'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (error) setError('');
  };

  const validateForm = () => {
    const required = ['organisationName', 'email', 'country', 'companyType', 'aiSystemStage'];
    const missing = required.filter(field => !formData[field]);
    
    if (missing.length > 0) {
      setError('Please fill in all required fields');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Save organisation details to localStorage or send to API
      const organisationData = {
        ...formData,
        assessmentType,
        timestamp: new Date().toISOString()
      };

      localStorage.setItem('organisationDetails', JSON.stringify(organisationData));

      // Navigate to survey
      navigate('/survey', { 
        state: { 
          assessmentType,
          organisationData
        }
      });

    } catch (err) {
      console.error('Error saving organisation details:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (assessmentType === 'expert') {
      navigate('/login');
    } else {
      navigate('/');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 2 }}>
        <Container>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton 
              onClick={handleBack}
              sx={{ color: 'white', mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              RAISE Index Assessment - Organization Details
            </Typography>
          </Box>
          
          {/* Progress Stepper */}
          <Stepper activeStep={1} alternativeLabel sx={{ mt: 2 }}>
            {steps.map((label, idx) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': { color: 'white !important' },
                    '& .MuiStepIcon-root': {
                      color: idx <= 1 ? '#fff !important' : undefined
                    },
                    '& .MuiStepIcon-text': {
                      fill: idx <= 1 ? '#00438f' : undefined
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
            Tell Us About Your Organization
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Please provide some basic information about your organization and AI system. 
            This helps us tailor the assessment to your specific context.
          </Typography>

          {assessmentType === 'expert' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You've selected the Expert-led Assessment. After completing this form, 
              you'll proceed to the detailed questionnaire, and our experts will review your responses.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Organisation Name */}
              <Grid item xs={12}>
                <TextField
                  name="organisationName"
                  label="Organization Name"
                  fullWidth
                  required
                  value={formData.organisationName}
                  onChange={handleInputChange}
                  disabled={loading}
                  helperText="Enter your company or organization name"
                />
              </Grid>

              {/* Email Address */}
              <Grid item xs={12} md={6}>
                <TextField
                  name="email"
                  label="Contact Email Address"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading}
                  helperText="We'll send your results to this email"
                />
              </Grid>

              {/* Country */}
              <Grid item xs={12} md={6}>
                <TextField
                  name="country"
                  label="Country"
                  select
                  fullWidth
                  required
                  value={formData.country}
                  onChange={handleInputChange}
                  disabled={loading}
                  helperText="Where is your organization based?"
                >
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Company Type */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    name="companyType"
                    label="Company Type"
                    select
                    fullWidth
                    required
                    value={formData.companyType}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {companyTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title="This helps us understand the scale and context of your AI implementation">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>

              {/* Industry Focus */}
              <Grid item xs={12} md={6}>
                <TextField
                  name="industryFocus"
                  label="Industry Focus"
                  select
                  fullWidth
                  value={formData.industryFocus}
                  onChange={handleInputChange}
                  disabled={loading}
                  helperText="What industry does your AI system serve?"
                >
                  {industryFocus.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* AI System Stage */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    name="aiSystemStage"
                    label="AI System Development Stage"
                    select
                    fullWidth
                    required
                    value={formData.aiSystemStage}
                    onChange={handleInputChange}
                    disabled={loading}
                    helperText="What stage is your AI system currently at?"
                  >
                    {aiSystemStages.map((stage) => (
                      <MenuItem key={stage} value={stage}>
                        {stage}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title="The assessment will be tailored based on your current development stage">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>

              {/* AI System Description */}
              <Grid item xs={12}>
                <TextField
                  name="aiSystemDescription"
                  label="AI System Description (Optional)"
                  multiline
                  rows={4}
                  fullWidth
                  value={formData.aiSystemDescription}
                  onChange={handleInputChange}
                  disabled={loading}
                  helperText="Brief description of your AI system, its purpose, and key features"
                  placeholder="e.g., A machine learning model for customer service chatbot that handles billing inquiries and technical support requests..."
                />
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                onClick={handleBack}
                disabled={loading}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ minWidth: 200 }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  'Continue to Assessment'
                )}
              </Button>
            </Box>
          </form>
        </Paper>

        {/* Additional Information */}
        <Paper elevation={1} sx={{ p: 3, mt: 3, bgcolor: 'grey.50', maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            What happens next?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            After completing this form, you'll be guided through the RAISE Index questionnaire. 
            The questions are designed to evaluate your AI system across key principles:
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            <Typography component="li" variant="body2">
              <strong>R</strong>esponsibility - Clear accountability and governance
            </Typography>
            <Typography component="li" variant="body2">
              <strong>A</strong>ccountability - Mechanisms for oversight and control
            </Typography>
            <Typography component="li" variant="body2">
              <strong>I</strong>nclusivity - Fair and unbiased AI systems
            </Typography>
            <Typography component="li" variant="body2">
              <strong>S</strong>ustainability - Environmental and social impact
            </Typography>
            <Typography component="li" variant="body2">
              <strong>E</strong>thics - Moral and ethical considerations
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}